import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { XIcon } from 'lucide-react';
import { useAnalytics } from 'analytics';
import { ANALYTICS_EVENTS } from 'analytics/events';
import { useAudioDevice } from 'providers/AudioDeviceProvider';
import type { AudioDeviceState } from 'utils/audio/audioDeviceTypes';
import { messageForState } from './audioMessages';
import { STATE_ICONS } from './stateIcons';
import {
  StatusBarActions,
  StatusBarBox,
  StatusBarCloseButton,
  StatusBarContent,
  StatusBarDescription,
  StatusBarInlineAction,
  StatusBarText,
  StatusBarTitle,
} from './AudioStatusBar.styles';

const SUCCESS_MESSAGE_DURATION_MS = 4_000;
const SEARCHING_NOTICE_DELAY_MS = 1_500;
const AUDIO_SETTINGS_ROUTE = '/audio-settings';
// The recording screen has its own bottom bar with its own banners.
const RECORDING_ROUTE_PREFIX = '/recording';

interface AudioStatusBarProps {
  successDurationMs?: number;
  searchingDelayMs?: number;
}

// Closing the bar must not drop the keyboard focus on the document body.
const focusPage = () => document.querySelector<HTMLElement>('main')?.focus();

export function AudioStatusBar({
  successDurationMs = SUCCESS_MESSAGE_DURATION_MS,
  searchingDelayMs = SEARCHING_NOTICE_DELAY_MS,
}: AudioStatusBarProps) {
  const audio = useAudioDevice();
  const { t } = useTranslation();
  const { trackEvent } = useAnalytics();
  const navigate = useNavigate();
  const location = useLocation();
  const [expiredKey, setExpiredKey] = useState<string | null>(null);
  const [searchingNoticeDue, setSearchingNoticeDue] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  const message = useMemo(
    () => (audio.mode === 'active' ? messageForState(audio, t) : null),
    [audio, t]
  );
  const messageKey = message ? `${audio.state}:${message.title}` : null;

  const trackEventRef = useRef(trackEvent);
  trackEventRef.current = trackEvent;
  useEffect(() => {
    if (audio.mode !== 'active' || audio.state === 'passive' || audio.state === 'searching') return;
    trackEventRef.current(ANALYTICS_EVENTS.AUDIO_DEVICE_STATE_CHANGED, {
      state: audio.state,
      isRecommended: audio.state === 'recommendedConnected',
    });
  }, [audio.mode, audio.state]);

  // A transient message stays as long as the keyboard focus is inside it.
  useEffect(() => {
    setExpiredKey(null);
    if (!message?.transient || messageKey === null) return;
    const box = boxRef.current;
    const expire = () => setExpiredKey(messageKey);
    let waitingForBlur = false;
    const timer = setTimeout(() => {
      if (box?.contains(document.activeElement)) {
        waitingForBlur = true;
        box.addEventListener('focusout', expire, { once: true });
      } else {
        expire();
      }
    }, successDurationMs);
    return () => {
      clearTimeout(timer);
      if (waitingForBlur) box?.removeEventListener('focusout', expire);
    };
  }, [message?.transient, messageKey, successDurationMs]);

  useEffect(() => {
    if (audio.state !== 'searching') {
      setSearchingNoticeDue(false);
      return;
    }
    const timer = setTimeout(() => setSearchingNoticeDue(true), searchingDelayMs);
    return () => clearTimeout(timer);
  }, [audio.state, searchingDelayMs]);

  const visible =
    message !== null &&
    messageKey !== null &&
    audio.state !== 'passive' &&
    !location.pathname.startsWith(RECORDING_ROUTE_PREFIX) &&
    !audio.messageDismissed &&
    (audio.state !== 'searching' || searchingNoticeDue) &&
    !(message.transient && expiredKey === messageKey);

  const isUrgent = message?.tone === 'warning' || message?.tone === 'error';

  // The switch replaces the button with the success message, so the focus moves to the bar itself.
  const switchToRecommended = async () => {
    try {
      await audio.switchToRecommendedDevice();
    } catch {
      toast.error(t('audio.settings.saveFailed'));
    } finally {
      boxRef.current?.focus();
    }
  };

  const runAction = () => {
    if (message?.action === 'open-settings') void navigate(AUDIO_SETTINGS_ROUTE);
    if (message?.action === 'switch-to-recommended') void switchToRecommended();
  };

  const dismiss = () => {
    audio.dismissMessage();
    focusPage();
  };

  const bar = visible && message && audio.state !== 'passive' && (
    <StatusBarBox
      ref={boxRef}
      tabIndex={-1}
      $tone={audio.state === 'searching' ? 'searching' : message.tone}
    >
      <StatusBarText>
        <BarIcon state={audio.state} />
        <StatusBarContent>
          <StatusBarTitle>{message.title}</StatusBarTitle>
          {message.description && (
            <StatusBarDescription>{message.description}</StatusBarDescription>
          )}
          {message.action && (
            <StatusBarInlineAction $tone={message.tone} onClick={runAction}>
              {message.action === 'open-settings'
                ? t('audio.actions.openSettings')
                : t('audio.actions.switchToRecommended')}
            </StatusBarInlineAction>
          )}
        </StatusBarContent>
      </StatusBarText>
      {audio.state !== 'searching' && (
        <StatusBarActions>
          <StatusBarCloseButton onClick={dismiss} aria-label={t('audio.messages.dismiss')}>
            <XIcon className="size-5" aria-hidden />
          </StatusBarCloseButton>
        </StatusBarActions>
      )}
    </StatusBarBox>
  );

  // Both live regions exist from the start so that a message is announced when it appears.
  return (
    <>
      <div role="status">{!isUrgent && bar}</div>
      <div role="alert">{isUrgent && bar}</div>
    </>
  );
}

const BarIcon = ({ state }: { state: AudioDeviceState }) => {
  const Icon = STATE_ICONS[state];
  const searching = state === 'searching';
  return (
    <Icon
      className={`size-4 shrink-0 ${searching ? 'animate-spin motion-reduce:animate-none' : 'mt-0.5'}`}
      aria-hidden
    />
  );
};
